import PropTypes from 'prop-types';
import React from 'react';

import styles from './map.scss';

const propTypes = {
  seatType: PropTypes.shape({
    name: PropTypes.string,
    fake: PropTypes.bool,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    data: PropTypes.shape({
      color: PropTypes.string.isRequired,
      fill: PropTypes.string.isRequired,
    }).isRequired,
  }),
};

const padlock = 'M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-10 0v-4c0-2.206 1.794-4 4-4s4 1.794 4 4v4h-8z';

const createSvgs = (props) => {
  const { seatType } = props;
  if (!seatType) {
    return null;
  }
  const {
    height,
    width,
    data,
  } = seatType;
  const {
    type,
    path,
    radius,
    fill,
    color,
  } = data;

  // Turn rotations into positive angles only
  const rotation = (360 + data.rotation) % 360;

  // Calculate the total space needed for rotated objects
  const th = rotation * (Math.PI / 180);
  const rotatedWidth = Math.abs(height * Math.sin(th)) + Math.abs(width * Math.cos(th));
  const rotatedHeight = Math.abs(width * Math.sin(th)) + Math.abs(height * Math.cos(th));

  // The math behind positioning rotated shapes differs based on the rotation
  // due to where the axis of the rotation is (top left corner).
  let translateX = 0;
  let translateY = 0;

  if (rotation > 270) {
    translateX = 0;
    translateY = rotatedHeight - (Math.cos(th) * height);
  } else if (rotation > 180) {
    translateX = Math.abs(Math.cos(th) * width);
    translateY = rotatedHeight;
  } else if (rotation > 90) {
    translateX = rotatedWidth;
    translateY = Math.abs(Math.cos(th) * height);
  } else {
    translateX = rotatedWidth - (Math.cos(th) * width);
    translateY = 0;
  }

  const rotationWrapper = rotation
    ? [`<g transform="translate(${translateX}, ${translateY}) rotate(${rotation})">`, '</g>']
    : ['', ''];

  // rotated measurements should be the same as original with 0 rotation
  const wrapper = [`<svg xmlns="http://www.w3.org/2000/svg" width="${rotatedWidth}" height="${rotatedHeight}">`, '</svg>'];
  const style = {
    original: `fill="${fill}"`,
    unavailable: 'fill="rgb(128, 128, 128)"',
  };

  const shapeWrapper = [];

  if (type.value === 'rect') {
    shapeWrapper.push(`${wrapper[0]}${rotationWrapper[0]}<rect width="${width}" height="${height}" rx="${radius}" ry="${radius}"`);
    shapeWrapper.push('></rect>');
  } else if (type.value === 'ellipse') {
    shapeWrapper.push(`${wrapper[0]}${rotationWrapper[0]}<ellipse cx="${width / 2}" cy="${height / 2}" rx="${width / 2}" ry="${height / 2}"`);
    shapeWrapper.push('></ellipse>');
  } else if (type.value === 'path') {
    shapeWrapper.push(`${wrapper[0]}${rotationWrapper[0]}<path d="${path}"`);
    shapeWrapper.push('></path>');
  } else {
    return null;
  }

  const padlockScale = (Math.min(width, height) / 24) * 0.7;
  const padlockOffsetX = (width / 2) - (12 * padlockScale);
  const padlockOffsetY = (height / 2) - (12 * padlockScale);

  return [
    ['original', `${shapeWrapper[0]} ${style.original} ${shapeWrapper[1]}${rotationWrapper[1]}${wrapper[1]}`],
    ['unavailable', `${shapeWrapper[0]} ${style.unavailable} ${shapeWrapper[1]} ${rotationWrapper[1]}${wrapper[1]}`],
    ['protected', `${shapeWrapper[0]} ${style.original} ${shapeWrapper[1]} <g transform="translate(${padlockOffsetX},${padlockOffsetY}) scale(${padlockScale},${padlockScale})"><path fill="${color}" d="${padlock}"/></g> ${rotationWrapper[1]}${wrapper[1]}`],
    ['protectedunavailable', `${shapeWrapper[0]} ${style.unavailable} ${shapeWrapper[1]} <g transform="translate(${padlockOffsetX},${padlockOffsetY}) scale(${padlockScale},${padlockScale})"><path fill="${color}" d="${padlock}"/></g> ${rotationWrapper[1]}${wrapper[1]}`],
  ];
};

const SeatType = React.forwardRef((props, ref) => {
  const svgs = createSvgs(props);
  const { name, fake } = props.seatType;

  const maybeHideStyle = fake ? { display: 'none' } : {};

  return (
    <div className={styles.seatTypes} ref={ref} style={maybeHideStyle}>
      <div className={styles.seatTypeSymbols}>
        {svgs.map((type) => (
          <img
            key={type[0]}
            className={type[0]}
            alt={type}
            src={`data:image/svg+xml;base64,${window.btoa(type[1])}`}
          />
        ))}
      </div>
      <div className={styles.seatTypeLabel}>{name}</div>
    </div>
  );
});

SeatType.propTypes = propTypes;

export default SeatType;
